import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Tag,
  User,
  ShieldCheck,
  ArrowLeft,
  Wifi,
  WifiOff,
  AlertTriangle,
} from "lucide-react";
import ClassificationTab from "../../ClassificationTab";
import OwnershipTab from "../../OwnershipTab";
import {
  classifyAssetApi,
  getAssetOwnershipApi,
  getInventoryConnectorsByIdApi,
} from "../../../../services/axios/AuthApis/AssetManagementApis";
import toast from "../../../../utils/notify";
import ConnectorDashboards from "../../ConnectorDashboards";
import TabNavigation from "../../../ui/TabNavigation";
import ApiLoader from "../../../ui/ApiLoader";
import PageLoader from "../../../ui/PageLoader";

const TABS = [
  { id: "dashboard",       name: "Dashboard",         icon: LayoutDashboard },
  { id: "classification",  name: "Classification",    icon: Tag             },
  { id: "ownership",       name: "Ownership",         icon: User            },
  { id: "compliance",      name: "Compliance Status", icon: ShieldCheck     },
];

const criticalityColor = (c) => {
  if (!c) return "bg-gray-100 text-gray-600";
  const v = c.toLowerCase();
  if (v === "critical") return "bg-red-100 text-red-700";
  if (v === "high")     return "bg-orange-100 text-orange-700";
  if (v === "medium")   return "bg-yellow-100 text-yellow-700";
  return "bg-green-100 text-green-700";
};

const AssetDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [asset, setAsset] = useState(null);
  const [assetLoading, setAssetLoading] = useState(false);

  const [classifiedAsset, setClassifiedAsset] = useState(null);
  const [isClassifying, setIsClassifying] = useState(false);

  const [ownership, setOwnership] = useState(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        setAssetLoading(true);
        const response = await getInventoryConnectorsByIdApi(id);
        setAsset(response);
      } catch {
        toast.error("Failed to load asset details");
      } finally {
        setAssetLoading(false);
      }
    };
    fetchAsset();
  }, [id]);

  const handleTabChange = async (tab) => {
    setActiveTab(tab);

    if (tab === "classification" && !classifiedAsset && !isClassifying) {
      try {
        setIsClassifying(true);
        const response = await classifyAssetApi(id);
        setClassifiedAsset(response);
        toast.success(`Asset classified successfully`);
      } catch {
        toast.error("Failed to classify asset");
      } finally {
        setIsClassifying(false);
      }
    }

    if (tab === "ownership" && !ownership && !ownershipLoading) {
      try {
        setOwnershipLoading(true);
        const response = await getAssetOwnershipApi(id);
        setOwnership(response?.ownership);
        toast.success(`Ownership fetched`);
      } catch {
        toast.error("Failed to fetch ownership");
      } finally {
        setOwnershipLoading(false);
      }
    }

    if (tab === "compliance") {
      navigate(`/compliance?asset=${id}`);
    }
  };

  if (assetLoading) {
    return <PageLoader title="Loading Asset details..." />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Back + Header */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
                >
                  {asset?.connector?.name?.[0]?.toUpperCase() ?? "A"}
                </div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {asset?.connector?.name ?? "—"}
                </h1>
              </div>
              <p className="text-sm text-gray-500 ml-12">
                Manage dashboard, compliance, classification and ownership.
              </p>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 sm:justify-end">
              {asset?.connector?.type && (
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                  {asset.connector.type}
                </span>
              )}
              {asset?.connector?.category && (
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                  {asset.connector.category}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                  asset?.connected
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {asset?.connected ? (
                  <Wifi className="w-3.5 h-3.5" />
                ) : (
                  <WifiOff className="w-3.5 h-3.5" />
                )}
                {asset?.connected ? "Connected" : "Not Connected"}
              </span>
              {asset?.criticality && (
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${criticalityColor(asset.criticality)}`}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {asset.criticality}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + Content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <TabNavigation
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        <div className="mt-2">
          {activeTab === "dashboard" && (
            <ConnectorDashboards data={asset} />
          )}
          {activeTab === "classification" && (
            isClassifying
              ? <ApiLoader label="Classifying asset..." />
              : <ClassificationTab classification={classifiedAsset} loading={false} />
          )}
          {activeTab === "ownership" && (
            ownershipLoading
              ? <ApiLoader label="Fetching ownership..." />
              : <OwnershipTab ownership={ownership} loading={false} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetDetailsPage;
